function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import cloneDeep from 'lodash/cloneDeep';
import React, { useState } from 'react';
import produce from 'immer';
import { useStagedState } from 'common/hooks/useStagedState';
import { MetricEntry, MetricEntryBehavior } from 'common/query-builder/query-entry-section/types';
import { DraggableEntry } from 'common/components/draggable-list';
import { BookmarkDragMetadata } from 'common/report/insights/contexts/drag-drop/types';
import { MetricType } from 'common/types/reports/bookmark';
import type { RowProps, UpdateEntryOpts } from '../types/catalog';
import { BehaviorEventRow, BehaviorProfileRow, BehaviorRowProps } from './event-row';

/**
 * Renders a BehaviorEventRow with drop targets above and below it for
 * changing the order of behavior steps
 */
export function DraggableBehavior(props: BehaviorRowProps & {
  isLast?: boolean;
  disableDrag?: boolean;
  disableSortZones?: boolean;
  /** when defined, marks draggable items as a single index group */
  indexOverride?: number;
}) {
  var _props$isLast, _props$indexOverride, _props$behaviorIndex2, _props$behaviorIndex3;
  const [stagedFilter, setStagedFilter] = useState<Obj | null>(null);
  const isUserBehavior = props.behavior.type === MetricType.Cohort || props.behavior.type === MetricType.People;
  const canAcceptFilter = (data: BookmarkDragMetadata) => {
    var _props$behaviorIndex;
    const filterGroup = "".concat(props.entry._idx, "-").concat((_props$behaviorIndex = props.behaviorIndex) !== null && _props$behaviorIndex !== void 0 ? _props$behaviorIndex : props.index, "-inline-filters");
    return filterGroup !== data.group;
  };
  const BehaviorRow = isUserBehavior ? BehaviorProfileRow : BehaviorEventRow;
  return <DraggableEntry<BookmarkDragMetadata> useDropRef disabled={props.disableDrag} disableSortZones={props.disableSortZones} isLast={(_props$isLast = props.isLast) !== null && _props$isLast !== void 0 ? _props$isLast : false} data={{
    type: "behavior-event",
    index: (_props$indexOverride = props.indexOverride) !== null && _props$indexOverride !== void 0 ? _props$indexOverride : props.index,
    group: props.entry._idx!,
    metricIndex: props.metricIndex,
    behaviorIndex: (_props$behaviorIndex2 = props.behaviorIndex) !== null && _props$behaviorIndex2 !== void 0 ? _props$behaviorIndex2 : props.index,
    parentMetricIndex: props.parentMetricIndex,
    setStagedEntry: filter => setStagedFilter(filter)
  }} accepts={{
    /**
     * @todo(metrics) we need to implement more conditions here to disallow dropping incompatible filters across metrics
     * This issue exists in production as well
     */
    'global-filter': canAcceptFilter,
    'inline-filter': canAcceptFilter,
    breakdown: () => true
  }} data-sentry-element="DraggableEntry" data-sentry-component="DraggableBehavior" data-sentry-source-file="behaviors-list.tsx">
      <BehaviorRow {...props} behaviorIndex={(_props$behaviorIndex3 = props.behaviorIndex) !== null && _props$behaviorIndex3 !== void 0 ? _props$behaviorIndex3 : props.index} stagedFilter={stagedFilter} abortStagedFilter={() => setStagedFilter(null)} data-sentry-element="BehaviorRow" data-sentry-source-file="behaviors-list.tsx" />
    </DraggableEntry>;
}

/**
 * Hook to manage a list of behaviors for a MetricEntry.
 * @todo(metrics) tracking
 */
export function useBehaviorsList(entry: MetricEntry, onUpdate: RowProps[`updateEntry`]) {
  var _entry$behavior$behav;
  const [behaviors, setBehaviors] = useStagedState<MetricEntryBehavior[], UpdateEntryOpts>((_entry$behavior$behav = entry.behavior.behaviors) !== null && _entry$behavior$behav !== void 0 ? _entry$behavior$behav : [], (state, opts) => {
    onUpdate(produce(entry, draft => {
      draft.behavior.behaviors = state;
    }), opts);
  });
  const handleAddBehavior = (newBehavior: MetricEntryBehavior, opts?: UpdateEntryOpts) => {
    setBehaviors([...behaviors, newBehavior], true, opts);
  };
  const handleUpdateBehavior = (index: number, updatedBehavior: MetricEntryBehavior, opts?: UpdateEntryOpts) => {
    setBehaviors(produce(behaviors, draft => {
      draft[index] = updatedBehavior;
    }), true, opts);
  };
  const handleDeleteBehavior = (index: number, opts?: UpdateEntryOpts) => {
    setBehaviors(produce(behaviors, draft => {
      draft.splice(index, 1);
    }), true, opts);
  };
  const handleDuplicateBehavior = (index: number, opts?: UpdateEntryOpts) => {
    setBehaviors(produce(behaviors, draft => {
      const behavior = _objectSpread(_objectSpread({}, cloneDeep(draft[index])), {}, {
        renamed: undefined
      });
      draft.splice(index + 1, 0, behavior);
    }), true, opts);
  };
  return {
    behaviors,
    setBehaviors,
    handleAddBehavior,
    handleUpdateBehavior,
    handleDeleteBehavior,
    handleDuplicateBehavior
  };
}